import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";

import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import { makeStyles } from "@material-ui/core";

import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Autocomplete, {
	createFilterOptions,
} from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import useQueues from "../../hooks/useQueues";
import useConnections from "../../hooks/useConnections";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import { dispatchTicketUpdated } from "../../utils/ticketEvents";

const useStyles = makeStyles((theme) => ({
  maxWidth: {
    width: "100%",
  },
}));

const filterOptions = createFilterOptions({
	trim: true,
});

const nonTransferableConnectionChannels = ["webchat", "instagram", "messenger"];
const NO_TRANSFER_QUEUE = "__no_transfer_queue__";
const NO_TRANSFER_CONNECTION = "__no_transfer_connection__";

const canTransferConnection = connection =>
	!nonTransferableConnectionChannels.includes(connection?.channel);

const TransferTicketModal = ({ modalOpen, onClose, ticketid, ticketConnectionId }) => {
	const history = useHistory();
	const [options, setOptions] = useState([]);
	const [queues, setQueues] = useState([]);
	const [allQueues, setAllQueues] = useState([]);
	const [loading, setLoading] = useState(false);
	const [searchParam, setSearchParam] = useState("");
	const [selectedUser, setSelectedUser] = useState(null);
	const [selectedQueue, setSelectedQueue] = useState('');
	const [selectedWhatsapp, setSelectedWhatsapp] = useState(ticketConnectionId || '');
	const classes = useStyles();
	const { findAll: findAllQueues } = useQueues();
	const { loading: loadingConnections, connections } = useConnections(modalOpen ? "transfer-ticket" : "closed");

	const { user: loggedInUser } = useContext(AuthContext);
	const ticketConnection = connections.find(
		connection => String(connection.id) === String(ticketConnectionId)
	);
	const connectionTransferEnabled =
		!ticketConnection || canTransferConnection(ticketConnection);
	const transferableConnections = connections.filter(canTransferConnection);
	const queueConnectionFilter = connectionTransferEnabled
		? selectedWhatsapp
		: ticketConnectionId;

	useEffect(() => {
		const loadQueues = async () => {
			const list = await findAllQueues();
			setAllQueues(list);
			setQueues(filterQueuesByWhatsapp(list, ticketConnectionId));
		}
		loadQueues();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ticketConnectionId]);

	useEffect(() => {
		if (!modalOpen) return;
		setSelectedWhatsapp(ticketConnectionId || '');
		setSelectedQueue('');
		setQueues(filterQueuesByWhatsapp(allQueues, ticketConnectionId));
	}, [modalOpen, ticketConnectionId, allQueues]);

	useEffect(() => {
		if (!modalOpen || loadingConnections || connectionTransferEnabled) return;
		setSelectedWhatsapp('');
		setSelectedQueue('');
		setQueues(filterQueuesByWhatsapp(allQueues, ticketConnectionId));
	}, [
		modalOpen,
		loadingConnections,
		connectionTransferEnabled,
		allQueues,
		ticketConnectionId,
	]);

	const filterQueuesByWhatsapp = (queueList, connectionId) => {
		if (!connectionId) return queueList;

		return queueList.filter(queue =>
			String(queue.connectionId || queue.connection?.id || '') === String(connectionId)
		);
	};

	useEffect(() => {
		if (!modalOpen || searchParam.length < 3) {
			setLoading(false);
			return;
		}
		setLoading(true);
		const delayDebounceFn = setTimeout(() => {
			const fetchUsers = async () => {
				try {
					const { data } = await api.get("/users/", {
						params: { searchParam },
					});
					setOptions(data.users);
					setLoading(false);
				} catch (err) {
					setLoading(false);
					toastError(err);
				}
			};

			fetchUsers();
		}, 500);
		return () => clearTimeout(delayDebounceFn);
	}, [searchParam, modalOpen]);

	const handleClose = () => {
		onClose();
		setSearchParam("");
		setSelectedUser(null);
	};

	const handleSaveTicket = async e => {
		e.preventDefault();
		if (!ticketid) return;
		setLoading(true);
		try {
			let data = {};

			if (selectedUser) {
				data.userId = selectedUser.id
			}

			if (selectedQueue && selectedQueue !== null) {
				data.queueId = selectedQueue

				if (!selectedUser) {
					data.status = 'pending';
					data.userId = null;
				}
			}

			if(connectionTransferEnabled && selectedWhatsapp) {
				data.connectionId = selectedWhatsapp;
			}

			const { data: updatedTicket } = await api.put(`/tickets/${ticketid}`, data);
			dispatchTicketUpdated(updatedTicket);

			setLoading(false);
			history.push(`/tickets`);
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
	};

	return (
		<Dialog open={modalOpen} onClose={handleClose} maxWidth="lg" scroll="paper">
			<form onSubmit={handleSaveTicket}>
				<DialogTitle id="form-dialog-title">
					{i18n.t("transferTicketModal.title")}
				</DialogTitle>
				<DialogContent dividers>
					<Autocomplete
						style={{ width: 300, marginBottom: 20 }}
						getOptionLabel={option => `${option.name}`}
						onChange={(e, newValue) => {
							setSelectedUser(newValue);
							if (newValue != null && Array.isArray(newValue.queues)) {
								setQueues(filterQueuesByWhatsapp(newValue.queues, queueConnectionFilter));
							} else {
								setQueues(filterQueuesByWhatsapp(allQueues, queueConnectionFilter));
								setSelectedQueue('');
							}
						}}
						options={options}
						filterOptions={filterOptions}
						freeSolo
						autoHighlight
						noOptionsText={i18n.t("transferTicketModal.noOptions")}
						loading={loading}
						renderInput={params => (
							<TextField
								{...params}
								label={i18n.t("transferTicketModal.fieldLabel")}
								variant="outlined"
								autoFocus
								onChange={e => setSearchParam(e.target.value)}
								InputProps={{
									...params.InputProps,
									endAdornment: (
										<React.Fragment>
											{loading ? (
												<CircularProgress color="inherit" size={20} />
											) : null}
											{params.InputProps.endAdornment}
										</React.Fragment>
									),
								}}
							/>
						)}
					/>
					<FormControl variant="outlined" className={classes.maxWidth}>
						<InputLabel>{i18n.t("transferTicketModal.fieldQueueLabel")}</InputLabel>
						<Select
							value={selectedQueue || NO_TRANSFER_QUEUE}
							onChange={(e) =>
								setSelectedQueue(
									e.target.value === NO_TRANSFER_QUEUE ? "" : e.target.value
								)
							}
							label={i18n.t("transferTicketModal.fieldQueuePlaceholder")}
						>
							<MenuItem value={NO_TRANSFER_QUEUE}>Sin departamento</MenuItem>
							{queues.map((queue) => (
								<MenuItem key={queue.id} value={queue.id}>{queue.name}</MenuItem>
							))}
						</Select>
					</FormControl>
					<Can
						role={loggedInUser.profile}
						perform="ticket-options:transferWhatsapp"
						yes={() => (!loadingConnections && connectionTransferEnabled &&
							<FormControl variant="outlined" className={classes.maxWidth} style={{ marginTop: 20 }}>
								<InputLabel>{i18n.t("transferTicketModal.fieldConnectionLabel")}</InputLabel>
								<Select
									value={selectedWhatsapp || NO_TRANSFER_CONNECTION}
									onChange={(e) => {
										const connectionId =
											e.target.value === NO_TRANSFER_CONNECTION ? "" : e.target.value;
										setSelectedWhatsapp(connectionId);
										setSelectedQueue('');
										setQueues(
											filterQueuesByWhatsapp(
												selectedUser?.queues && Array.isArray(selectedUser.queues)
													? selectedUser.queues
													: allQueues,
												connectionId
											)
										);
									}}
									label={i18n.t("transferTicketModal.fieldConnectionPlaceholder")}
								>
									<MenuItem value={NO_TRANSFER_CONNECTION}>Sin conexion</MenuItem>
									{transferableConnections.map((whasapp) => (
										<MenuItem key={whasapp.id} value={whasapp.id}>
											{whasapp.name || whasapp.number || `Conexión ${whasapp.id}`}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						)}
					/>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={handleClose}
						color="secondary"
						disabled={loading}
						variant="outlined"
					>
						{i18n.t("transferTicketModal.buttons.cancel")}
					</Button>
					<ButtonWithSpinner
						variant="contained"
						type="submit"
						color="primary"
						loading={loading}
					>
						{i18n.t("transferTicketModal.buttons.ok")}
					</ButtonWithSpinner>
				</DialogActions>
			</form>
		</Dialog>
	);
};

export default TransferTicketModal;

